import { z } from 'zod';

import { idSchema } from './id';
import { changeLogEventActorSchema } from './job-architecture-history';

const countSchema = z.number().int().nonnegative();

const skillBulkRemovalCountsSchema = z.object({
    employees: countSchema,
    learningOpportunities: countSchema,
    levels: countSchema,
    roles: countSchema,
    tasks: countSchema.nullish().transform((tasks) => tasks ?? 0),
});

const removedSkillReferenceSchema = z.object({
    id: idSchema,
    title: z.string(),
});

/**
 * The two tenant-wide scopes are selected per removal, so the receipt echoes the choice: a `0` count
 * for a selected scope means "nothing was attached", not "not requested".
 */
const skillBulkRemovalScopesSchema = z.object({
    employees: z.boolean(),
    learningOpportunities: z.boolean(),
    tasks: z
        .boolean()
        .nullish()
        .transform((tasks) => tasks ?? false),
});

/**
 * The removal clears the skill from every version of every profile in the selected architectures in a
 * single transaction, so `POST /skills/{skillId}/bulk-remove` always answers `200` with the finished
 * receipt. `operationId` is generated per execution and links the changelog rows the same transaction
 * wrote.
 */
export const skillBulkRemovalReceiptSchema = z.object({
    architectureIds: idSchema.array().readonly(),
    counts: skillBulkRemovalCountsSchema,
    operationId: idSchema,
    removedAt: z.iso.datetime(),
    removedBy: changeLogEventActorSchema.nullish(),
    scopes: skillBulkRemovalScopesSchema,
    skill: removedSkillReferenceSchema,
});

export type SkillBulkRemovalReceipt = z.infer<typeof skillBulkRemovalReceiptSchema>;

/** Selection-table budget: the request carries at most this many architectures. */
const MAX_REQUESTED_ARCHITECTURES = 100;

/**
 * Every scope is selected explicitly, so both booleans are required and the id list may be empty: an
 * employees-only removal touches no architecture at all. A request that selects nothing is rejected,
 * because "no scope" would otherwise read as a silent no-op the caller cannot distinguish from success.
 */
export const bulkRemoveSkillRequestSchema = z
    .object({
        architectureIds: idSchema.array().max(MAX_REQUESTED_ARCHITECTURES).readonly(),
        employees: z.boolean(),
        learningOpportunities: z.boolean(),
        tasks: z.boolean(),
    })
    // `tasks` is deliberately not a selection on its own: tasks only exist within the selected architectures.
    .refine(
        (request) => request.architectureIds.length > 0 || request.learningOpportunities || request.employees,
        'a removal has to select at least one architecture, learning opportunities or employees',
    );

export type BulkRemoveSkillRequest = z.infer<typeof bulkRemoveSkillRequestSchema>;
